import { useCallback, useEffect, useRef, useState } from 'react';
import { useMeetingState, type MeetingAgent, type Opinion, type HumanInput } from '../hooks/useMeetingState';
import MarkdownRenderer from './MarkdownRenderer';

// ─── Styles (injected once) ──────────────────────────────────────────────────

const MEETING_STYLES = `
@keyframes meetingPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
@keyframes meetingBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.meeting-opinions-scroll::-webkit-scrollbar { width: 4px; }
.meeting-opinions-scroll::-webkit-scrollbar-track { background: transparent; }
.meeting-opinions-scroll::-webkit-scrollbar-thumb { background: var(--theme-muted); border-radius: 2px; }
`;

// ─── Sub-Components ──────────────────────────────────────────────────────────

function AgentBadge({ agent, isActive, isDropped }: {
  agent: MeetingAgent;
  isActive: boolean;
  isDropped: boolean;
}) {
  return (
    <div
      title={`${agent.label}${isDropped ? ' (dropped out)' : isActive ? ' (active)' : ''}`}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        background: isActive
          ? 'var(--theme-accent)'
          : 'var(--theme-secondary)',
        opacity: isDropped ? 0.3 : isActive ? 1 : 0.4,
        border: isActive
          ? '2px solid var(--theme-primary)'
          : '2px solid transparent',
        transition: 'all 0.3s ease',
        position: 'relative',
      }}
    >
      {agent.emoji}
      {isDropped && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '10%',
          right: '10%',
          height: '2px',
          background: 'var(--theme-muted-foreground)',
          transform: 'rotate(-45deg)',
        }} />
      )}
    </div>
  );
}

function OpinionCard({ opinion, isStreaming }: {
  opinion: Opinion;
  isStreaming?: boolean;
}) {
  const isDropout = opinion.isDropout;

  if (isDropout) {
    return (
      <div style={{
        background: 'var(--theme-secondary)',
        borderRadius: '12px',
        padding: '12px 16px',
        borderLeft: '4px solid var(--theme-muted)',
        opacity: 0.6,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ fontSize: '24px' }}>{opinion.emoji}</span>
        <span style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.7rem',
          fontStyle: 'italic',
          color: 'var(--theme-muted-foreground)',
        }}>
          {opinion.label} dropped out of the meeting
        </span>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--theme-card)',
      borderRadius: '12px',
      padding: '16px',
      borderLeft: isStreaming
        ? '4px solid var(--theme-success)'
        : '4px solid var(--theme-primary)',
      transition: 'border-color 0.3s ease',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <span style={{ fontSize: '24px' }}>{opinion.emoji}</span>
        <span style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.72rem',
          fontWeight: 700,
          color: 'var(--theme-foreground)',
        }}>
          {opinion.label}
        </span>
        <span style={{
          background: 'var(--theme-secondary)',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.6rem',
          fontWeight: 600,
          color: 'var(--theme-muted-foreground)',
          marginLeft: 'auto',
        }}>
          R{opinion.round}
        </span>
      </div>

      {/* Body */}
      <div style={{
        fontFamily: 'var(--app-code-font)',
        fontSize: '0.72rem',
        lineHeight: 1.65,
        color: 'var(--theme-card-foreground)',
      }}>
        <MarkdownRenderer content={opinion.text} isStreaming={isStreaming} />
        {isStreaming && (
          <span style={{
            display: 'inline-block',
            width: '7px',
            height: '14px',
            background: 'var(--theme-success)',
            marginLeft: '2px',
            verticalAlign: 'middle',
            animation: 'meetingBlink 0.8s infinite',
          }} />
        )}
      </div>
    </div>
  );
}

function HumanInputCard({ input }: { input: HumanInput }) {
  const emoji = input.speaker === 'feifei' ? '🎨' : '👤';
  const name = input.speaker.charAt(0).toUpperCase() + input.speaker.slice(1);

  return (
    <div style={{
      background: 'var(--theme-user-bubble-background)',
      borderRadius: '12px',
      padding: '16px',
      borderLeft: '4px solid var(--theme-warning)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <span style={{ fontSize: '24px' }}>{emoji}</span>
        <span style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.72rem',
          fontWeight: 700,
          color: 'var(--theme-foreground)',
        }}>
          {name}
        </span>
        <span style={{
          background: 'var(--theme-secondary)',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.6rem',
          fontWeight: 600,
          color: 'var(--theme-warning)',
        }}>
          Human
        </span>
      </div>
      <div style={{
        fontFamily: 'var(--app-code-font)',
        fontSize: '0.72rem',
        lineHeight: 1.65,
        color: 'var(--theme-card-foreground)',
      }}>
        {input.message}
      </div>
    </div>
  );
}

function HumanInputBar({ onSend, disabled }: {
  onSend: (speaker: string, message: string) => void;
  disabled?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [speaker, setSpeaker] = useState('jackie');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed) return;
    onSend(speaker, trimmed);
    setMessage('');
    inputRef.current?.focus();
  }, [message, speaker, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      padding: '12px 16px',
      borderTop: '1px solid var(--theme-border)',
      background: 'var(--theme-card)',
      alignItems: 'center',
    }}>
      <select
        value={speaker}
        onChange={(e) => setSpeaker(e.target.value)}
        disabled={disabled}
        style={{
          padding: '8px',
          borderRadius: '6px',
          background: 'var(--theme-secondary)',
          border: '1px solid var(--theme-border)',
          color: 'var(--theme-foreground)',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.68rem',
          cursor: 'pointer',
        }}
      >
        <option value="jackie">👤 Jackie</option>
        <option value="feifei">🎨 Feifei</option>
      </select>
      <input
        ref={inputRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Join the discussion..."
        disabled={disabled}
        style={{
          flex: 1,
          padding: '8px 12px',
          borderRadius: '6px',
          background: 'var(--theme-input)',
          border: '1px solid var(--theme-border)',
          color: 'var(--theme-foreground)',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.72rem',
          outline: 'none',
          transition: 'border-color 0.15s ease',
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--theme-primary) 30%, transparent)'; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--theme-border)'; }}
      />
      <button
        onClick={handleSend}
        disabled={disabled || !message.trim()}
        style={{
          padding: '8px 16px',
          borderRadius: '6px',
          border: 'none',
          background: 'var(--theme-primary)',
          color: 'var(--theme-primary-foreground)',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          cursor: 'pointer',
          opacity: disabled || !message.trim() ? 0.4 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        SEND
      </button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AgentMeetingView() {
  const { meetingState, loading, error, sendHumanInput } = useMeetingState();
  const feedEndRef = useRef<HTMLDivElement>(null);
  const prevOpinionCountRef = useRef(0);

  // Inject styles
  useEffect(() => {
    const existing = document.getElementById('meeting-view-styles');
    if (!existing) {
      const style = document.createElement('style');
      style.id = 'meeting-view-styles';
      style.textContent = MEETING_STYLES;
      document.head.appendChild(style);
    }
    return () => {
      document.getElementById('meeting-view-styles')?.remove();
    };
  }, []);

  // Auto-scroll when new opinions arrive
  useEffect(() => {
    const currentCount = meetingState.opinions.length;
    if (currentCount > prevOpinionCountRef.current) {
      feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevOpinionCountRef.current = currentCount;
  }, [meetingState.opinions.length]);

  const handleHumanInput = useCallback((speaker: string, message: string) => {
    void sendHumanInput(speaker, message);
  }, [sendHumanInput]);

  if (loading && meetingState.date === '') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--theme-background)',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
      }}>
        <div style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.72rem',
          color: 'var(--theme-muted-foreground)',
        }}>
          Loading meeting state...
        </div>
      </div>
    );
  }

  if (error && meetingState.date === '') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--theme-background)',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
      }}>
        <div style={{ fontSize: '2rem' }}>🎯</div>
        <div style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--theme-foreground)',
        }}>
          No meeting data yet
        </div>
        <div style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.65rem',
          color: 'var(--theme-muted-foreground)',
          textAlign: 'center',
          maxWidth: '320px',
          lineHeight: 1.6,
        }}>
          Run the daily retrospective script to start a meeting.
        </div>
      </div>
    );
  }

  const { date, status, currentRound, maxRounds, activeAgents, droppedAgents, agents, opinions, humanInputs } = meetingState;

  // Build interleaved feed: opinions + human inputs ordered by rounds
  const feedItems: Array<{ type: 'opinion'; data: Opinion } | { type: 'human'; data: HumanInput }> = [];

  // Group opinions by round, interleave human inputs
  const maxRoundSeen = opinions.length > 0 ? Math.max(...opinions.map(o => o.round)) : 0;
  for (let round = 1; round <= maxRoundSeen; round++) {
    // Human inputs that came before this round
    const humanBefore = (humanInputs || []).filter(h => h.beforeRound === round);
    for (const hi of humanBefore) {
      feedItems.push({ type: 'human', data: hi });
    }
    // Opinions for this round
    const roundOpinions = opinions.filter(o => o.round === round);
    for (const op of roundOpinions) {
      feedItems.push({ type: 'opinion', data: op });
    }
  }
  // Human inputs after the last round
  const humanAfterLast = (humanInputs || []).filter(h => !h.beforeRound || h.beforeRound > maxRoundSeen);
  for (const hi of humanAfterLast) {
    feedItems.push({ type: 'human', data: hi });
  }

  const statusLabel = status === 'in_progress' ? 'In Progress'
    : status === 'completed' ? 'Completed'
    : 'Waiting';

  const statusColor = status === 'in_progress' ? 'var(--theme-success)'
    : status === 'completed' ? 'var(--theme-primary)'
    : 'var(--theme-muted-foreground)';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--theme-background)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--theme-border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        background: 'var(--theme-card)',
      }}>
        <div style={{
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: 'var(--theme-foreground)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          🎯 Daily Retrospective
          {date && (
            <span style={{
              fontWeight: 400,
              color: 'var(--theme-muted-foreground)',
              fontSize: '0.65rem',
            }}>
              — {date}
            </span>
          )}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: 'var(--app-ui-font)',
          fontSize: '0.6rem',
          color: 'var(--theme-muted-foreground)',
        }}>
          {currentRound > 0 && (
            <span>Round {currentRound}/{maxRounds}</span>
          )}
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.58rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: statusColor,
            background: `color-mix(in srgb, ${statusColor} 12%, transparent)`,
            border: `1px solid color-mix(in srgb, ${statusColor} 20%, transparent)`,
          }}>
            {status === 'in_progress' && (
              <span style={{ animation: 'meetingPulse 1s infinite', fontSize: '8px' }}>●</span>
            )}
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Agent Status Bar */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '10px 16px',
        background: 'var(--theme-secondary)',
        borderBottom: '1px solid var(--theme-border)',
        flexShrink: 0,
        flexWrap: 'wrap',
      }}>
        {agents.map((agent) => (
          <AgentBadge
            key={agent.id}
            agent={agent}
            isActive={activeAgents.includes(agent.id)}
            isDropped={droppedAgents.includes(agent.id)}
          />
        ))}
        {agents.length === 0 && (
          <span style={{
            fontFamily: 'var(--app-ui-font)',
            fontSize: '0.6rem',
            color: 'var(--theme-muted-foreground)',
            padding: '8px 0',
          }}>
            No agents loaded
          </span>
        )}
      </div>

      {/* Opinions Feed */}
      <div
        className="meeting-opinions-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {feedItems.length === 0 && status === 'waiting' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: '12px',
            padding: '3rem',
          }}>
            <div style={{ fontSize: '2rem' }}>⏳</div>
            <div style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--theme-foreground)',
            }}>
              Waiting for meeting to start
            </div>
            <div style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.65rem',
              color: 'var(--theme-muted-foreground)',
              textAlign: 'center',
              lineHeight: 1.6,
            }}>
              The meeting will appear here once the retrospective script begins.
            </div>
          </div>
        )}

        {feedItems.length === 0 && status === 'in_progress' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: '12px',
            padding: '3rem',
          }}>
            <div style={{ fontSize: '2rem', animation: 'meetingPulse 1.5s infinite' }}>🎯</div>
            <div style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--theme-foreground)',
            }}>
              Meeting in progress...
            </div>
            <div style={{
              fontFamily: 'var(--app-ui-font)',
              fontSize: '0.65rem',
              color: 'var(--theme-muted-foreground)',
              textAlign: 'center',
              lineHeight: 1.6,
            }}>
              Agents are forming their opinions. Results will stream in shortly.
            </div>
          </div>
        )}

        {feedItems.map((item, i) => {
          if (item.type === 'human') {
            return <HumanInputCard key={`human-${i}`} input={item.data} />;
          }
          // Check if this is the last opinion and meeting is in progress (could be streaming)
          const isLast = i === feedItems.length - 1 && status === 'in_progress';
          return (
            <OpinionCard
              key={`opinion-${item.data.agentId}-r${item.data.round}`}
              opinion={item.data}
              isStreaming={isLast}
            />
          );
        })}

        {status === 'completed' && feedItems.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '16px',
            fontFamily: 'var(--app-ui-font)',
            fontSize: '0.6rem',
            color: 'var(--theme-muted-foreground)',
            letterSpacing: '0.06em',
          }}>
            ─── Meeting completed ───
          </div>
        )}

        <div ref={feedEndRef} />
      </div>

      {/* Human Input Bar (only when meeting is in progress) */}
      {status === 'in_progress' && (
        <HumanInputBar onSend={handleHumanInput} />
      )}
    </div>
  );
}
